<template>
  <v-container fluid class="my-4 mx-3">
    <template v-if="isLoading">
      <v-row>
        <v-col md="12" lg="6">
          <v-skeleton-loader height="100%" type="list-item-avatar-three-line, image, table-tfoot" />
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <template v-if="hrAudit">
        <v-row class="mb-5">
          <v-col cols="12" lg="8">

            <v-alert v-if="showVideoBanner" type="info" border="left" colored-border elevation="1" dismissible
              class="mb-6">
              <div class="d-flex flex-column flex-sm-row justify-space-between align-center">
                <div>
                  <strong>Need help interpreting your results?</strong>
                  <div class="text--secondary">
                    Watch our quick video overview of your HR Health Check report.
                  </div>
                </div>
                <v-btn color="primary" class="mt-4 mt-sm-0" @click="showVideoDialog = true">
                  ▶️ Watch Now
                </v-btn>
              </div>
            </v-alert>

            <v-card width="700" color="grey lighten-4" class="mb-4">
              <v-card-title> {{ hrAudit.templateName }} - Report </v-card-title>
              <v-divider></v-divider>

              <v-card-text>
                <v-simple-table class="summary-table">
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="hrAudit.companyName">
                        <td>Company Name</td>
                        <td>{{ hrAudit.companyName }}</td>
                        <td></td>
                      </tr>
                      <tr v-if="hrAudit.companySize">
                        <td>Company Size</td>
                        <td>{{ hrAudit.companySize }}</td>
                        <td></td>
                      </tr>
                      <tr v-if="hrAudit.industry">
                        <td>Industry</td>
                        <td>{{ hrAudit.industry }}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Overall Score</td>
                        <td>{{ overallScore }}%</td>
                        <td>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-progress-circular v-bind="attrs" v-on="on" :rotate="360" :size="30" :width="5"
                                :value="overallScore" :color="overallScore === 100 ? 'success' : 'warning'"
                                class="mr-3"></v-progress-circular>
                            </template>
                            <span>{{ overallScore }}%</span>
                          </v-tooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Compliance Score
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" class="ml-2" size="medium"
                                color="info">info_outline</v-icon>
                            </template>
                            <span>Compliance items are required by Australian Employment Law</span>
                          </v-tooltip>
                        </td>
                        <td>{{ complianceScore }}%</td>
                        <td>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-progress-circular v-bind="attrs" v-on="on" :rotate="360" :size="30" :width="5"
                                :value="complianceScore" :color="complianceScore === 100 ? 'success' : 'warning'"
                                class="mr-3"></v-progress-circular>
                            </template>
                            <span>{{ complianceScore }}%</span>
                          </v-tooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>Link</td>
                        <td colspan="2">
                          <v-text-field id="audit_link" readonly :value="auditLink">
                            <template v-slot:append-outer>
                              <v-tooltip :close-delay="1800" bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon v-on="on" @click="copyLinkToClipboard">content_copy</v-icon>
                                </template>
                                {{ copyToClipboardText }}
                              </v-tooltip>
                            </template>
                          </v-text-field>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <v-alert v-if="totalIncompleteComplianceCount > 0" type="error" border="left" colored-border
                  icon="privacy_tip" elevation="2" class="my-4">
                  <strong>You have {{ totalIncompleteComplianceCount }}</strong>
                  unactioned item<span v-if="totalIncompleteComplianceCount > 1">s</span>
                  required for compliance with Australian Employment Law.
                </v-alert>

              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <span class="mr-2 grey--text">Created on {{ hrAudit.createdDateUtc | formatDateTime }}</span>
              </v-card-actions>
            </v-card>

            <v-card width="100%" text style="display:none">
              <VimeoPlayer videoId="1074090786" />
            </v-card>

            <template v-for="(group, index) in hrAudit.groups">
              <v-card :key="index" class="mb-8 mt-3">
                <v-card-title>
                  {{ group.name }}

                  <v-chip :color="getGroupHeaderColour(group)" dark class="ml-2 px-2" style="height: 22px">{{
                    getGroupCompletionPc(group) }}%</v-chip>
                </v-card-title>

                <v-card-text>
                  <v-alert :value="getGroupComplianceIncompleteCount(group) > 0" type="error" outlined
                    icon="privacy_tip" class="my-4 group-alert">
                    <span v-if="getGroupComplianceIncompleteCount(group) === 1">
                      <strong>There is 1</strong> unactioned compliance item in this section that is required by
                      Australian Employment Law.
                    </span>
                    <span v-else>
                      <strong>There are {{ getGroupComplianceIncompleteCount(group) }}</strong> unactioned compliance
                      items in this section that are required by Australian Employment Law.
                    </span>
                  </v-alert>
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Answer</th>
                          <th class="text-left">Question</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in group.items" :key="item.id" :class="{
                          'compliance-incomplete':
                            item.result == false && item.isComplianceQuestion, 'incomplete':
                            item.result == false, 'complete': item.result == true
                        }">
                          <td>
                            <v-tooltip bottom v-if="item.result == true">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-if="item.result == true" v-bind="attrs" v-on="on"
                                  :color="getResultButtonColour(item)">check_circle</v-icon>
                              </template>
                              <span>Answer: Yes</span>
                            </v-tooltip>

                            <v-tooltip bottom v-else-if="item.isComplianceQuestion">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon :color="getResultButtonColour(item)" v-bind="attrs" v-on="on">cancel</v-icon>
                              </template>
                              <span>Answer: No
                                <div class="font-italic">
                                  This is required by Australian Employment Law
                                </div>
                              </span>
                            </v-tooltip>

                            <v-tooltip bottom v-else>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon :color="getResultButtonColour(item)" v-bind="attrs" v-on="on">cancel</v-icon>
                              </template>
                              <span>Answer: No</span>
                            </v-tooltip>
                          </td>
                          <td>

                            <div v-if="item.isComplianceQuestion" class="mt-2">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <span v-bind="attrs" class="caption" :class="getComplianceTextClass(item)" v-on="on">
                                    <v-icon class="mr-1" size="medium" :color="getComplianceIconColor(item)">
                                      privacy_tip
                                    </v-icon>
                                    Compliance
                                  </span>
                                </template>
                                <span>This is required by Australian Employment Law.</span>
                              </v-tooltip>
                            </div>

                            <div v-else class="mt-2">
                              <span class="caption" :class="getBestPracticeTextClass(item)">
                                <v-icon class="mr-1" size="medium" :color="getBestPracticeIconColor(item)">
                                  mdi-medal
                                </v-icon>
                                Best Practice
                              </span>
                            </div>

                            <div class="my-2">{{ item.question }}</div>
                            <v-alert :value="item.comments != null && item.comments.length > 0" class="mt-3 caption"
                              color="cyan" border="left" elevation="2" colored-border icon="chat" dense>
                              {{ item.comments }}
                            </v-alert>

                            <v-alert v-if="item.result == false && item.actionableSteps && item.actionableSteps.length"
                              color="success" border="left" colored-border elevation="2" dense class="mt-3 caption">
                              <strong class="d-block mb-1">Next Steps</strong>
                              <ul class="pl-4 mb-0">
                                <li v-for="(step, index) in item.actionableSteps" :key="index">
                                  {{ step.step }}
                                </li>
                              </ul>
                            </v-alert>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </template>

            <v-card v-if="hrAudit.comments" class="mr-4 mt-4">
              <v-card-subtitle><v-icon class="mr-2" size="medium">article</v-icon>Submission Notes</v-card-subtitle>
              <v-card-text>{{ hrAudit.comments }}</v-card-text>
            </v-card>
          </v-col>

          <!-- Right: Next Steps Sidebar -->
          <v-col cols="12" lg="4">

            <div class="sticky-next-steps">

              <v-card class="next-steps-card" outlined elevation="1">
                <v-card-title class="headline font-weight-medium">Next Steps</v-card-title>
                <v-divider class="mb-4"></v-divider>
                <v-card-text>

                  <!-- Download Section -->
                  <div class="next-step mb-5">
                    <h4 class="subtitle-1 font-weight-bold mb-1">📄 Download Your Personalised HR Strategy Starter</h4>
                    <p class="mb-2">We've created a tailored HR Strategy Starter Template based on your HR Health Check
                      results,
                      highlighting your specific gaps and priorities.</p>
                    <v-btn color="primary" @click="downloadActionReport" :loading="isDownloadingActionReport" block>
                      <v-icon left class="mr-2">mdi-download</v-icon>
                      Download for Free
                    </v-btn>
                  </div>

                  <!-- Consultant Booking Section -->
                  <div class="next-step">
                    <h4 class="subtitle-1 font-weight-bold mb-3">📞 Book a Free HR Session</h4>
                    <p class="mb-4">Choose who you'd like to meet with for a personalised walk-through of your results:
                    </p>

                    <v-row dense>
                      <v-col cols="12" sm="6" v-for="consultant in consultants" :key="consultant.name">
                        <v-card outlined class="pa-3 text-center">
                          <v-avatar size="64" class="mx-auto mb-2">
                            <img :src="consultant.photo" :alt="consultant.name" />
                          </v-avatar>
                          <div class="font-weight-medium mb-1">{{ consultant.name }}</div>
                          <div class="caption grey--text mb-3">{{ consultant.title }}</div>
                          <v-btn color="secondary" outlined block @click="openCalendly(consultant.link)">
                            Book with {{ consultant.firstName }}
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                </v-card-text>
              </v-card>

            </div>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <v-alert type="info" icon="info">
          No audit data available.
        </v-alert>
      </template>
    </template>

    <v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn :color="snackbar.buttonColor" v-bind="attrs" text @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Modal Dialog (Vimeo) -->
    <v-dialog v-model="showVideoDialog" width="800px">
      <v-card>
        <v-card-text class="pt-5">
          <div class="video-wrapper">
            <iframe src="https://player.vimeo.com/video/1074090786?title=0&byline=0&portrait=0"
              allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="showVideoDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCalendlyDialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card class="pa-0">
        <v-toolbar flat dark color="primary">
          <v-btn icon dark @click="showCalendlyDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Book Your Session</v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- Consultant picker (desktop toolbar) -->
          <v-select v-model="selectedConsultantUrl" :items="consultants" item-text="name" item-value="link" hide-details
            dense outlined class="ma-2" style="max-width: 280px" label="Choose consultant" />
        </v-toolbar>

        <v-card-text class="pa-0">
          <!-- Consultant picker (mobile) -->
          <div class="pa-3 d-sm-none">
            <v-select v-model="selectedConsultantUrl" :items="consultants" item-text="name" item-value="link" filled
              dense label="Choose consultant" />
          </div>

          <div class="calendly-container">
            <iframe :key="computedCalendlySrc" :src="computedCalendlySrc" width="100%" height="100%" frameborder="0"
              scrolling="no"></iframe>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>


    <v-row justify="center" class="mt-10" v-if="hrAudit">
      <v-col cols="12" md="8" class="text-center">
        <small class="text--secondary">
          <strong>Disclaimer:</strong> This HR Health Check is intended as a general guide only and does not constitute
          legal advice.
          Please seek tailored legal or HR advice before making any significant decisions based on this report.
        </small>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import hrAuditApi from "@/api/hrAudit.api";
import VimeoPlayer from '@/components/VimeoPlayer.vue'
import steve from '@/assets/consultants/Steve.jpeg'
import celine from '@/assets/consultants/Celine.jpeg'
import sarah from '@/assets/consultants/Sarah.png'

export default {
  components: {
    VimeoPlayer
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hrAudit: null,
      isLoading: false,
      snackbar: {
        show: false,
        message: "",
        color: undefined,
        buttonColor: undefined,
        timeout: 6000,
      },
      copyToClipboardText: "Copy to clipboard",
      showVideoBanner: true,
      showVideoDialog: false,
      showCalendlyDialog: false,
      isDownloadingActionReport: false,
      consultants: [
        {
          name: "Steve Luxmoore",
          firstName: "Steve",
          title: "HR Consultant",
          link: "https://calendly.com/steve-from-your-hr/30min?hide_event_type_details=1&hide_gdpr_banner=1",
          photo: steve
        },
        {
          name: "Celine Pirotte",
          firstName: "Celine",
          title: "HR Consultant",
          link: "https://calendly.com/celine-pirotte-yourhrteam/30min?hide_event_type_details=1&hide_gdpr_banner=1",
          photo: celine
        },
        {
          name: "Sarah Welti",
          firstName: "Sarah",
          title: "HR Consultant",
          link: "https://calendly.com/sarah-welti-yourhrteam/30min?hide_event_type_details=1&hide_gdpr_banner=1",
          photo: sarah
        }
      ],
      selectedConsultantUrl: 'https://calendly.com/steve-from-your-hr',
    };
  },
  async created() {
    try {
      this.isLoading = true;
      this.hrAudit = await hrAuditApi.getById(this.id);
    } catch (ex) {
      if (ex.response && ex.response.status === 404) {
        this.showSnackbar("HR Audit could not be found", true);
      } else {
        this.showSnackbar("Failed to load", true);
      }
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    computedCalendlySrc() {
      const params = 'hide_event_type_details=1&hide_gdpr_banner=1';
      // Preserve any existing querystring from the consultant URL
      const hasQuery = this.selectedConsultantUrl.includes('?');
      return `${this.selectedConsultantUrl}${hasQuery ? '&' : '?'}${params}`;
    },
    totalScorableQuestionCount() {
      if (!this.hrAudit) return 0;
      return this.hrAudit.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          return this.$_isScorableQuestion(currentItem, this.hrAudit)
            ? groupTotal + 1
            : groupTotal;
        }, allTotal);
      }, 0);
    },
    totalComplianceQuestionCount() {
      if (!this.hrAudit) return 0;
      return this.hrAudit.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          return groupTotal + (currentItem.isComplianceQuestion ? 1 : 0);
        }, allTotal);
      }, 0);
    },
    totalIncompleteComplianceCount() {
      if (!this.hrAudit) return 0;
      return this.hrAudit.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          if (currentItem.result == false && currentItem.isComplianceQuestion) {
            return groupTotal + 1;
          } else return groupTotal;
        }, allTotal);
      }, 0);
    },
    overallScore() {
      if (!this.hrAudit) return 0;

      const complete = this.hrAudit.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          const answered = currentItem.result == true;
          return groupTotal + (answered ? 1 : 0);
        }, allTotal);
      }, 0);

      if (complete === 0 || this.totalScorableQuestionCount === 0) return 0;

      return Math.round((complete / this.totalScorableQuestionCount) * 100);
    },
    complianceScore() {
      if (!this.hrAudit) return 0;

      const complete = this.hrAudit.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          const answered =
            currentItem.isComplianceQuestion && currentItem.result == true;
          return groupTotal + (answered ? 1 : 0);
        }, allTotal);
      }, 0);

      if (complete === 0 || this.totalComplianceQuestionCount === 0) return 0;

      return Math.round((complete / this.totalComplianceQuestionCount) * 100);
    },
    auditLink() {
      return `https://healthcheck.yourhrsystem.com/${this.id}`;
    }
  },
  methods: {
    showSnackbar(message, isError) {
      this.snackbar.show = true;
      this.snackbar.message = message;
      this.snackbar.color = isError ? "red accent-2" : undefined;
      this.snackbar.timeout = isError ? 0 : 6000;
    },
    getGroupHeaderColour(group) {
      const completionPc = this.getGroupCompletionPc(group);
      if (completionPc === 100) return "success";
      else return "warning";
    },
    getGroupCompletionPc(group) {
      const complete = group.items.filter((item) => item.result == true).length;
      const total = group.items.length;

      if (complete === 0 || total === 0) return 0;

      return Math.round((complete / total) * 100);
    },
    getResultButtonColour({ questionType, result }) {
      if (questionType === 2) return "default";
      if (result == true) return "success";
      return questionType === 0 ? "warning" : "error";
    },
    getGroupComplianceIncompleteCount(group) {
      return group.items.filter(
        (item) => item.result == false && item.isComplianceQuestion
      ).length;
    },
    getComplianceTextClass(item) {
      if (item.questionType === 2) return 'grey--text text--darken-1'; // Neutral
      if (item.result === true) return 'success--text';
      return 'error--text';
    },
    getComplianceIconColor(item) {
      if (item.questionType === 2) return 'grey';
      if (item.result === true) return 'success';
      return 'error';
    },
    getBestPracticeTextClass(item) {
      if (item.questionType === 2) return 'grey--text text--darken-1'; // Neutral
      if (item.result === true) return 'success--text';
      return 'warning--text';
    },
    getBestPracticeIconColor(item) {
      if (item.questionType === 2) return 'grey';
      if (item.result === true) return 'success';
      return 'warning';
    },
    copyLinkToClipboard() {
      // Get the text field
      var copyText = document.getElementById("audit_link");

      // Select the text field
      copyText.select();
      copyText.setSelectionRange(0, 99999); // For mobile devices

      // Copy the text inside the text field
      navigator.clipboard.writeText(copyText.value);

      this.copyToClipboardText = "Copied to clipboard!";
    },
    async downloadActionReport() {
      this.isDownloadingActionReport = true;
      await hrAuditApi.downloadActionReport(this.id);
      this.isDownloadingActionReport = false;
    },
    openCalendly(link) {
      this.selectedConsultantUrl = link;
      this.showCalendlyDialog = true;
    }
  },
};
</script>

<style scoped>
tr.complete {
  background-color: rgba(0, 255, 0, 0.1);
}

tr.complete:hover {
  background-color: rgba(0, 255, 0, 0.1) !important;
}

tr.complete td:first-child {
  border-left: 5px solid #4caf50;
  /* Green */
}

tr.incomplete {
  background-color: rgba(255, 193, 7, 0.08);
}

tr.incomplete:hover {
  background-color: rgba(255, 193, 7, 0.08) !important;
}

tr.incomplete td:first-child {
  border-left: 5px solid #ffc107;
  /* Amber */
}

tr.compliance-incomplete {
  background-color: rgba(255, 82, 82, 0.04);
}

tr.compliance-incomplete td:first-child {
  border-left: 5px solid #ff5252;
}

tr.compliance-incomplete:hover {
  background-color: rgba(255, 82, 82, 0.04) !important;
}

.summary-table tr>td:first-child {
  width: 185px;
}

.sticky-next-steps {
  position: sticky;
  top: 50px;
  z-index: 2;
}

.next-steps-card {
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.next-step h4 {
  margin-bottom: 4px;
}

.next-step p {
  font-size: 14px;
  color: #555;
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
}

.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.calendly-container {
  width: 100%;
  height: calc(100vh - 64px);
  /* Adjust depending on your toolbar height */
  overflow: hidden;
}

.calendly-card {
  width: 100%;
  max-width: 900px;
}
</style>
